// Phase 3.1d — Inventory Stocks toolbar.
//
// Replaces THIS screen's use of the shared antd/MUI <ApparelSubmenu> (the
// printSticker===true branch) with a shadcn toolbar: debounced search, status
// <Select>, download / stickering-complete actions, +Add, server pagination, and
// the select-all confirm dialog. The shared ApparelSubmenu is left untouched for
// the other six screens that still use it.
//
// Behaviour parity: every dispatched filter/pagination/bulk-update call is the
// same as ApparelSubmenu (same handlers via props; barcode zip + navigate kept
// internal, exactly as the legacy component did). Loose-equality status keys are
// preserved. Radix <Select> forbids empty-string item values, so the "All"
// option uses an internal ALL sentinel mapped back to "" on dispatch.
import * as React from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Download, Printer, Search } from "lucide-react";
import AppConstant from "../../Globals/AppConstants";
import { getBarcodeZipFile } from "../../Store/actions/apparelAction";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "ui/select";
import { Combobox } from "ui/combobox";
import { Button } from "ui/button";
import { Input } from "ui/input";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "ui/dialog";
import { cn } from "lib/utils";

const ALL = "__all__";
const NO_VENDOR = "__no_vendor__";

export interface InventoryToolbarProps {
  title: string;
  searchPlaceHolder: string;
  /** Opens the Add Product sheet (parent's setOpen(true)). */
  onAdd: () => void;
  /** Still needed for the select-all confirm dialog's >10-records gate. */
  totalPage: number;
  multipleUpdateChanges: (key: any) => void;
  completedSticker: boolean;
  setCompletedSticker: (v: boolean) => void;
  downloadSticker: boolean;
  setDownloadSticker: (v: boolean) => void;
  setInstockSticker: (v: boolean) => void;
  checkRecords: any[];
  setCheckRecords: (v: any) => void;
  filterStatus: any;
  getFilterStatusdataList: (v: any) => void;
  mrpFilter?: any;
  mrpList?: any[];
  getMrpFilterdataList?: (v: any) => void;
  vendorFilter?: any;
  vendorList?: any[];
  getVendorFilterdataList?: (v: any) => void;
  categoryFilter?: any;
  subCategoryFilter?: any;
  categoryAndSubCategoryList?: any[];
  getCategoryFilterdataList?: (v: any) => void;
  getSubCategoryFilterdataList?: (v: any) => void;
  /** Stock Type filter: "" (All), 1 = Regular, 2 = Combo. */
  stockTypeFilter?: any;
  getStockTypeFilterdataList?: (v: any) => void;
  getsearchTextdataList: (v: any) => void;
  defaultSearchText?: any;
  selectAll: (e: any) => void;
  setArrayStatus: (v: number) => void;
  selectAllModal: boolean;
  updateSpinner: boolean;
  setUpdateSpinner: (v: boolean) => void;
}

function debounce(callback: any, delay: number) {
  let time: any;
  return (...args: any) => {
    clearTimeout(time);
    time = setTimeout(() => callback(...args), delay);
  };
}

// Normal-mode status options (legacy keys/labels, "" -> ALL sentinel).
const STATUS_OPTIONS = [
  { value: ALL, label: "All Status" },
  // { value: "1", label: "Yet to start" },
  // { value: "6", label: AppConstant?.completedSticker },
  { value: "5", label: AppConstant?.damaged },
  { value: "2", label: AppConstant?.inStock },
  { value: "10", label: AppConstant?.outOfStock },
  // { value: "4", label: AppConstant?.returned },
  // { value: "3", label: AppConstant?.sold },
  // { value: "7", label: AppConstant?.missed },
  // { value: "9", label: AppConstant?.scrap },
];

// Stock Type filter (productDetails.stockType; "" -> ALL sentinel).
const STOCK_TYPE_OPTIONS = [
  { value: ALL, label: "All Stock Types" },
  { value: "1", label: "Regular" },
  { value: "2", label: "Combo" },
];

// Download-mode status options (legacy keys/labels, "0" == All -> [1,2,4,5,6]).
const DOWNLOAD_STATUS_OPTIONS = [
  { value: "0", label: "All" },
  { value: "1", label: "Yet to start" },
  { value: "6", label: AppConstant?.completedSticker },
  { value: "5", label: AppConstant?.damaged },
  { value: "2", label: AppConstant?.inStock },
  { value: "4", label: AppConstant?.returned },
];

export function InventoryToolbar({
  title,
  searchPlaceHolder,
  onAdd,
  totalPage,
  multipleUpdateChanges,
  completedSticker,
  setCompletedSticker,
  downloadSticker,
  setDownloadSticker,
  setInstockSticker,
  checkRecords,
  setCheckRecords,
  filterStatus,
  getFilterStatusdataList,
  mrpFilter,
  mrpList,
  getMrpFilterdataList,
  vendorFilter,
  vendorList,
  getVendorFilterdataList,
  categoryFilter,
  subCategoryFilter,
  categoryAndSubCategoryList,
  getCategoryFilterdataList,
  getSubCategoryFilterdataList,
  stockTypeFilter,
  getStockTypeFilterdataList,
  getsearchTextdataList,
  defaultSearchText,
  selectAll,
  setArrayStatus,
  selectAllModal,
  updateSpinner,
  setUpdateSpinner,
}: InventoryToolbarProps) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const apparelReducerState: any = useSelector((state: any) => state.ApparelReducerState);
  const { updateProductStatusOnload } = apparelReducerState;

  const [selectAllModalOpen, setSelectAllModalOpen] = React.useState(false);
  const [completedStickerSelected, setCompletedStickerSelected] = React.useState(1);

  const mrpComboOptions = React.useMemo(() => {
    const opts = (mrpList || [])
      .map((m: any) => m?.mrp)
      .filter((v: any) => v !== null && v !== undefined)
      .map((v: any) => ({ value: String(v), label: `₹ ${v}` }));
    return [{ value: ALL, label: "All Price" }, ...opts];
  }, [mrpList]);

  const vendorComboOptions = React.useMemo(() => {
    const opts = (vendorList || [])
      .filter((v: any) => v?.vendorId !== null && v?.vendorId !== undefined)
      .map((v: any) => ({
        value: String(v.vendorId),
        label: v?.name + (v?.contactName ? " - " + v?.contactName : ""),
      }));
    return [{ value: ALL, label: "All Vendor" }, { value: NO_VENDOR, label: "No Vendor" }, ...opts];
  }, [vendorList]);

  const categoryComboOptions = React.useMemo(() => {
    const opts = (categoryAndSubCategoryList || []).map((c: any) => ({
      value: String(c?.categoryId),
      label: c?.categoryName,
    }));
    return [{ value: ALL, label: "All Category" }, ...opts];
  }, [categoryAndSubCategoryList]);

  // Mirrors AddProductSheet's category -> sub-category cascade: only the
  // selected category's sub-categories are offered here.
  const subCategoryComboOptions = React.useMemo(() => {
    const opts: any[] = [];
    (categoryAndSubCategoryList || [])
      .filter((c: any) => categoryFilter && categoryFilter !== ALL && String(c?.categoryId) === String(categoryFilter))
      .forEach((c: any) =>
        (c?.subCategories || []).forEach((s: any) =>
          opts.push({ value: String(s?.subCategoryId), label: s?.subCategoryName })
        )
      );
    return [{ value: ALL, label: "All Sub-Category" }, ...opts];
  }, [categoryAndSubCategoryList, categoryFilter]);

  // getsearchTextdataList closes over the parent's current filterData (so it
  // can spread ...filterData, searchText: ... onto the next setFilterData
  // call), so a fresh one arrives as a prop on every filter change. The
  // debounced wrapper below must stay a single stable instance across
  // renders (so a burst of keystrokes shares one timer) while still calling
  // whatever the LATEST getsearchTextdataList is — otherwise typing after
  // selecting Status/MRP/Vendor would fire the mount-time closure and
  // silently revert those filters back to their initial ("All") values.
  const getsearchTextdataListRef = React.useRef(getsearchTextdataList);
  React.useEffect(() => {
    getsearchTextdataListRef.current = getsearchTextdataList;
  }, [getsearchTextdataList]);

  const handledebounceChange = React.useMemo(
    () => debounce((value: any) => getsearchTextdataListRef.current(value), 500),
    []
  );

  React.useEffect(() => {
    setCompletedStickerSelected(1);
  }, [completedSticker]);

  React.useEffect(() => {
    if (!updateProductStatusOnload && updateSpinner) {
      setUpdateSpinner(false);
    }
  }, [updateProductStatusOnload, updateSpinner]); // eslint-disable-line react-hooks/exhaustive-deps

  // --- legacy handlers, verbatim -------------------------------------------
  const download = () => {
    dispatch(getBarcodeZipFile(checkRecords));
    navigate("/barcode/list");
  };

  const close = () => {
    setCompletedSticker(false);
    setDownloadSticker(false);
    setCheckRecords([]);
    getFilterStatusdataList(""); // filterStatusRemove(3)
  };

  const filterStatusRemove = (key: any) => {
    if (key === 1) {
      setDownloadSticker(true);
      if (filterStatus === "") getFilterStatusdataList([1, 2, 4, 5, 6]);
    } else if (key === 2) {
      setCompletedSticker(true);
      getFilterStatusdataList("1");
    }
  };

  const downloadFilterStatus = (key: any) => {
    if (key === "0") getFilterStatusdataList([1, 2, 4, 5, 6]);
    else getFilterStatusdataList(key);
  };

  const handleCompletedTab = (data: any) => {
    setCompletedStickerSelected(data);
    if (data === 2) {
      setInstockSticker(true);
      getFilterStatusdataList("6");
    } else {
      setInstockSticker(false);
      getFilterStatusdataList("1");
    }
  };

  const handleOnChangeSelectAll = (e: any) => {
    if (e.target.value === "selectPage") {
      selectAll(e);
      setArrayStatus(0);
    } else if (e.target.value === "selectAllPage") {
      setArrayStatus(1);
    }
  };

  const handleModalOk = () => {
    multipleUpdateChanges(completedStickerSelected === 1 ? "5" : "3");
    setSelectAllModalOpen(false);
    setUpdateSpinner(true);
  };

  const handleCancel = () => {
    setCheckRecords([]);
    setSelectAllModalOpen(false);
  };

  const normalMode = !completedSticker && !downloadSticker;
  const bulkDisabled = (checkRecords?.length ?? 0) === 0;

  return (
    <div className="mz flex flex-col gap-3 border-b bg-background px-4 py-3">
      {/* -------- row 1: title, search, actions -------- */}
      <div className="flex flex-wrap items-center gap-3">
        {/* -------- title / sticker tabs / back -------- */}
        <div className="flex shrink-0 items-center gap-3">
          {downloadSticker ? (
            <>
              <button type="button" onClick={close} aria-label="Back" className="text-muted-foreground hover:text-foreground">
                <ArrowLeft className="h-5 w-5" />
              </button>
              <span className="text-base font-semibold">Download Sticker</span>
            </>
          ) : completedSticker ? (
            <>
              <button type="button" onClick={close} aria-label="Back" className="text-muted-foreground hover:text-foreground">
                <ArrowLeft className="h-5 w-5" />
              </button>
              <button
                type="button"
                onClick={() => handleCompletedTab(1)}
                className={cn(
                  "rounded-md px-3 py-1.5 text-sm font-medium",
                  completedStickerSelected === 1 ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-muted"
                )}
              >
                Stickering
              </button>
              <button
                type="button"
                onClick={() => handleCompletedTab(2)}
                className={cn(
                  "rounded-md px-3 py-1.5 text-sm font-medium",
                  completedStickerSelected === 2 ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-muted"
                )}
              >
                In Stock
              </button>
            </>
          ) : (
            <span className="text-base font-semibold">{title}</span>
          )}
        </div>

        <div className="relative w-full sm:w-64">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            className="pl-9"
            placeholder={searchPlaceHolder}
            defaultValue={defaultSearchText || ""}
            onChange={(e) => handledebounceChange(e.target.value)}
          />
        </div>

        <div className="ml-auto flex flex-wrap items-center gap-2">
          {normalMode ? (
            <>
              {/* <Button variant="outline" size="icon" aria-label="Download sticker" onClick={() => filterStatusRemove(1)}>
                <Download className="h-4 w-4" />
              </Button> */}
              {/* <Button variant="outline" size="icon" aria-label="Stickering completed" onClick={() => filterStatusRemove(2)}>
                <Printer className="h-4 w-4" />
              </Button> */}
              <Button onClick={onAdd}>{AppConstant?.add}</Button>
            </>
          ) : (
            <>
              {downloadSticker && (
                <Select
                  value={
                    filterStatus && filterStatus !== ""
                      ? typeof filterStatus !== "object"
                        ? String(filterStatus)
                        : "0"
                      : undefined
                  }
                  onValueChange={(v) => downloadFilterStatus(v)}
                >
                  <SelectTrigger className="w-[150px]"><SelectValue placeholder="Status" /></SelectTrigger>
                  <SelectContent>
                    {DOWNLOAD_STATUS_OPTIONS.map((o) => (
                      <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
              <Button
                disabled={bulkDisabled}
                onClick={() =>
                  downloadSticker
                    ? download()
                    : completedStickerSelected < 3 && selectAllModal && totalPage > 10
                    ? setSelectAllModalOpen(true)
                    : handleModalOk()
                }
              >
                {downloadSticker ? "Download Sticker" : completedStickerSelected === 1 ? "Complete Stickering" : "In Stock"}
              </Button>
            </>
          )}
        </div>
      </div>

      {/* -------- row 2: filters — wrap to the screen width, never scroll -------- */}
      {normalMode && (
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-6">
          <Select
            value={filterStatus && filterStatus !== "" ? String(filterStatus) : ALL}
            onValueChange={(v) => getFilterStatusdataList(v === ALL ? "" : v)}
          >
            <SelectTrigger className="w-full"><SelectValue placeholder="Status" /></SelectTrigger>
            <SelectContent>
              {STATUS_OPTIONS.map((o) => (
                <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          {getMrpFilterdataList && (
            <Combobox
              className="w-full"
              options={mrpComboOptions}
              value={mrpFilter && mrpFilter !== "" ? String(mrpFilter) : ALL}
              onValueChange={(v) => getMrpFilterdataList(v === ALL ? "" : v)}
              placeholder="MRP"
              searchPlaceholder="Search MRP..."
            />
          )}
          {getVendorFilterdataList && (
            <Combobox
              className="w-full"
              options={vendorComboOptions}
              value={vendorFilter && vendorFilter !== "" ? String(vendorFilter) : ALL}
              onValueChange={(v) => getVendorFilterdataList(v === ALL ? "" : v)}
              placeholder="Vendor"
              searchPlaceholder="Search vendor..."
            />
          )}
          {getCategoryFilterdataList && (
            <Combobox
              className="w-full"
              options={categoryComboOptions}
              value={categoryFilter && categoryFilter !== "" ? String(categoryFilter) : ALL}
              onValueChange={(v) => getCategoryFilterdataList(v === ALL ? "" : v)}
              placeholder="Category"
              searchPlaceholder="Search category..."
            />
          )}
          {getSubCategoryFilterdataList && (
            <Combobox
              className="w-full"
              options={subCategoryComboOptions}
              value={subCategoryFilter && subCategoryFilter !== "" ? String(subCategoryFilter) : ALL}
              onValueChange={(v) => getSubCategoryFilterdataList(v === ALL ? "" : v)}
              placeholder="Sub-Category"
              searchPlaceholder="Search sub-category..."
            />
          )}
          {getStockTypeFilterdataList && (
            <Select
              value={stockTypeFilter && stockTypeFilter !== "" ? String(stockTypeFilter) : ALL}
              onValueChange={(v) => getStockTypeFilterdataList(v === ALL ? "" : v)}
            >
              <SelectTrigger className="w-full"><SelectValue placeholder="Stock Type" /></SelectTrigger>
              <SelectContent>
                {STOCK_TYPE_OPTIONS.map((o) => (
                  <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </div>
      )}

      {/* -------- select-all confirm dialog -------- */}
      <Dialog open={selectAllModalOpen} onOpenChange={(o) => { if (!o) handleCancel(); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Do you want to select ?</DialogTitle>
          </DialogHeader>
          <div className="space-y-2">
            <label className="flex items-center gap-2 text-sm font-medium">
              <input type="radio" name="select" value="selectPage" onChange={handleOnChangeSelectAll} />
              Current page
            </label>
            <label className="flex items-center gap-2 text-sm font-medium">
              <input type="radio" name="select" value="selectAllPage" onChange={handleOnChangeSelectAll} />
              All page
            </label>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={handleCancel}>Cancel</Button>
            <Button onClick={handleModalOk}>OK</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default InventoryToolbar;
